<template>
  <div class="Wdgt-Navigation">
    <CompactAudioplayer class="Wdgt-Navigation__audioplayer"/>
    <div class="Wdgt-Navigation__wrapper">
      <nav class="Wdgt-Navigation__list">
        <div class="Wdgt-Navigation__caret" :style="get_caretStates" :class="{ 'no-transition': isCaretInstant }"></div>
        <button
          class="Wdgt-Navigation__btn"
          :class="buttonActiveClass(get_isNewsSection)"
          @click="activeInfonoise('news')"
          ref="newsBtn"
        >
          <NewsIcon/>
        </button>
        <button
          class="Wdgt-Navigation__btn"
          :class="buttonActiveClass(get_isAlphaSection)"
          @click="activeInfonoise('alpha')"
          ref="alphaBtn"
        >
          <AlphaIcon/>
        </button>
        <button
          class="Wdgt-Navigation__btn"
          :class="isMenuBtnActive ? 'Wdgt-Navigation__btn--active' : ''"
          @click="toggleMenu()"
          ref="menuBtn"
        >
          <component :is="isMenuOpen ? CloseIcon : AppIcon"/>
        </button>
        <transition name="menu-scale">
          <MenuNavigation
            v-if="isMenuOpen"
            class="Wdgt-Navigation__menu-overlay"
            @click.self="isMenuOpen = false"
          />
        </transition>
      </nav>
    </div>
  </div>
</template>

<script setup>
  import { computed, onMounted, onBeforeUnmount, ref, nextTick, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useStore } from 'vuex';
  import { throttle } from 'lodash';
  import { PATHS_NAME } from '@shared/routers/consts/pathsName';
  import CompactAudioplayer from '@/Groups/Audioplayer/public/modules/CompactAudioplayer/CompactAudioplayer.vue';
  import AlphaIcon from '@svg/navigation/AlphaIcon.vue';
  import NewsIcon from "@svg/navigation/NewsIcon.vue";
  import AppIcon from "@svg/navigation/AppIcon.vue";
  import MenuNavigation from "@widgets/NavigationWidget/components/Menu/MenuNavigation.vue";
  import CloseIcon from "@svg/navigation/CloseIcon.vue";

  const router = useRouter();
  const route = useRoute();
  const store = useStore();

  const ACTIVE_BTN_CLASS = 'Wdgt-Navigation__btn--active';

  const caretWidth = ref(0);
  const caretLeft = ref(0);
  const activeTag = ref(null);

  const newsBtn = ref(null);
  const alphaBtn = ref(null);
  const menuBtn = ref(null);

  const isCaretInstant = ref(true);
  const isMenuOpen = ref(false);
  const isKeyboardOpen = ref(false);

  const isMenuBtnActive = computed(() => activeTag.value === menuBtn.value);
  const get_isInfonoisePage = computed(() => route.name === PATHS_NAME.MAIN);
  const get_isNewsSection = computed(() => store.state.ContentSystem.isAdaptiveNews && get_isInfonoisePage.value);
  const get_isAlphaSection = computed(() => store.state.ContentSystem.isAdaptiveAlpha && get_isInfonoisePage.value);
  const get_isCalendarPage = computed(() => route.name === PATHS_NAME.CALENDAR);
  const get_isForecastersPage = computed(() => route.name === PATHS_NAME.FORECASTERS);
  const get_isSignalsPage = computed(() => route.name === PATHS_NAME.SIGNALS);
  const get_isPolymarketPage = computed(() => route.name === PATHS_NAME.POLYMARKET);

  const get_caretStates = computed(() => ({
    width: `${caretWidth.value}px`,
    left: `${caretLeft.value}px`
  }));

  const toggleMenu = () => {
    isMenuOpen.value = !isMenuOpen.value;
    nextTick(() => requestAnimationFrame(toMoveCaret));
  };

  const buttonActiveClass = (condition) => (condition ? ACTIVE_BTN_CLASS : '');

  const toFindActiveElement = () => {
    const candidates = [
      { condition: get_isNewsSection.value, element: newsBtn.value },
      { condition: get_isAlphaSection.value, element: alphaBtn.value },
      { condition: get_isCalendarPage.value, element: menuBtn.value },
      { condition: get_isForecastersPage.value, element: menuBtn.value },
      { condition: get_isSignalsPage.value, element: menuBtn.value },
      { condition: get_isPolymarketPage.value, element: menuBtn.value },
    ];
    const active = candidates.find(({ condition }) => condition);
    activeTag.value = active ? active.element : null;
  };

  const toCalcWidthCaret = (retry = 0) => {
    toFindActiveElement();
    if (!activeTag.value || activeTag.value.offsetWidth === 0) {
      if (retry < 5) setTimeout(() => toCalcWidthCaret(retry + 1), 100);
      return;
    }
    caretWidth.value = activeTag.value.offsetWidth;
  };

  const toMoveCaret = (retry = 0) => {
    toFindActiveElement();
    if (!activeTag.value) return;
    const relativeParent = activeTag.value.offsetParent;
    const childRect = activeTag.value.getBoundingClientRect();
    const parentRect = relativeParent?.getBoundingClientRect?.() || { left: 0 };
    const left = childRect.left - parentRect.left;
    if (left === 0 && retry < 5) {
      setTimeout(() => toMoveCaret(retry + 1), 100);
      return;
    }
    caretLeft.value = left;
  };

  const activeInfonoise = (type) => {
    window.scrollTo({ top: 0 });
    if (type === 'news') store.commit('ContentSystem/toChangeNewsVisible');
    else if (type === 'alpha') store.commit('ContentSystem/toChangeAlphaVisible');

    router.push({ name: PATHS_NAME.MAIN }).then(() => {
      nextTick(() => requestAnimationFrame(() => {
        toCalcWidthCaret();
        toMoveCaret();
      }));
    });
  };

  const handleClickOutsideMenu = (event) => {
    if (
      isMenuOpen.value &&
      !menuBtn.value?.contains(event.target) &&
      !document.querySelector('.Wdgt-Navigation__menu-overlay')?.contains(event.target)
    ) {
      isMenuOpen.value = false;
    }
  };

  // ------------------------------------------
  // УПРОЩЕННАЯ И НАДЕЖНАЯ ЛОГИКА ПОЗИЦИОНИРОВАНИЯ
  // ------------------------------------------
  let resizeObserver = null;

  // Основная функция для установки позиции навигации
  const setNavigationPosition = () => {
    const nav = document.querySelector('.Wdgt-Navigation');
    if (!nav) return;

    // ВАЖНО: Устанавливаем фиксированную позицию внизу
    // CSS уже должен иметь bottom: 0, но на всякий случай принудительно устанавливаем
    nav.style.bottom = '0px';
    
    // Обновляем CSS переменную для других элементов
    const navHeight = nav.offsetHeight;
    document.documentElement.style.setProperty('--nav-height', `${navHeight}px`);
  };

  // Обработчик клавиатуры
  const handleKeyboard = throttle(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const windowHeight = window.innerHeight;
    const viewportHeight = viewport.height;
    const keyboardHeight = windowHeight - viewportHeight;
    
    const keyboardOpened = keyboardHeight > 200;
    isKeyboardOpen.value = keyboardOpened;

    const nav = document.querySelector('.Wdgt-Navigation');
    if (!nav) return;

    if (keyboardOpened) {
      // Когда клавиатура открыта - поднимаем навигацию над ней
      nav.style.bottom = `${keyboardHeight}px`;
    } else {
      // Когда клавиатура закрыта - возвращаем вниз
      nav.style.bottom = '0px';
    }

    // Всегда обновляем высоту
    const navHeight = nav.offsetHeight;
    document.documentElement.style.setProperty('--nav-height', `${navHeight}px`);
  }, 150);

  const handleResize = throttle(() => {
    toCalcWidthCaret();
    toMoveCaret();
    setNavigationPosition();
  }, 200);

  onMounted(() => {
    // Инициализация позиции
    setNavigationPosition();
    
    // Основные слушатели
    window.addEventListener('resize', handleResize);
    document.addEventListener('click', handleClickOutsideMenu);

    // Слушатели для виртуального viewport (клавиатура)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleKeyboard);
      window.visualViewport.addEventListener('scroll', handleKeyboard);
    }

    // Обработчик изменения ориентации
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        setNavigationPosition();
        toCalcWidthCaret();
        toMoveCaret();
      }, 350);
    });

    // Обработчики фокуса для клавиатуры
    document.addEventListener('focusin', handleKeyboard);
    document.addEventListener('focusout', () => {
      setTimeout(handleKeyboard, 300);
    });

    // Восстановление позиции при возвращении на страницу
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        setTimeout(setNavigationPosition, 300);
      }
    });

    // Обновление при навигации
    router.afterEach(() => {
      toCalcWidthCaret();
      nextTick(() => {
        requestAnimationFrame(() => {
          toMoveCaret();
          setTimeout(setNavigationPosition, 100);
        });
      });
    });

    // ResizeObserver для кнопок
    resizeObserver = new ResizeObserver(() => {
      toCalcWidthCaret();
      toMoveCaret();
      setNavigationPosition();
    });

    [newsBtn, alphaBtn, menuBtn].forEach(btn => {
      if (btn.value) resizeObserver.observe(btn.value);
    });

    // Финальная инициализация
    nextTick(() => {
      setTimeout(() => {
        toCalcWidthCaret();
        toMoveCaret();
        isCaretInstant.value = false;
        setNavigationPosition();
      }, 200);
    });
  });

  onBeforeUnmount(() => {
    // Очистка всех слушателей
    window.removeEventListener('resize', handleResize);
    document.removeEventListener('click', handleClickOutsideMenu);

    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', handleKeyboard);
      window.visualViewport.removeEventListener('scroll', handleKeyboard);
    }

    window.removeEventListener('orientationchange', setNavigationPosition);
    document.removeEventListener('focusin', handleKeyboard);
    document.removeEventListener('focusout', handleKeyboard);

    resizeObserver?.disconnect?.();
  });

  watch(
    [() => isMenuOpen.value, () => route.fullPath],
    ([menuOpen, path], [prevOpen, prevPath]) => {
      if (path !== prevPath) isMenuOpen.value = false;
      nextTick(() => requestAnimationFrame(toMoveCaret));
    },
    { flush: 'post' }
  );
</script>

<style lang="scss">
@import "./styles/_styles";
</style>